import { useEffect, useRef, MutableRefObject } from 'react';
import { DlobWebsocketClient, MarketId, ENUM_UTILS } from '@drift/common';
import useOrderbookDisplayStore, {
	OrderbookSource,
	getBestAvailableOrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import useTabIsActive from 'src/hooks/utils/useTabIsActive';
import { useShallow } from 'zustand/react/shallow';
import {
	FALLBACK_COOLDOWN_MS,
	TAB_INACTIVE_DISCONNECT_DELAY_MS,
	RECOVERY_CHECK_INTERVAL_MS,
} from '../constants';

interface UseTabVisibilityAndRecoveryParams {
	selectedMarketId: MarketId | undefined;
	client: DlobWebsocketClient;
	clientRef: MutableRefObject<DlobWebsocketClient | null>;
	subscriptionRef: MutableRefObject<{ unsubscribe: () => void } | null>;
	currentSubscriptionMarketIdRef: MutableRefObject<MarketId | null>;
	currentSubscriptionGroupingRef: MutableRefObject<number | null>;
	lastFallbackTimestamp: MutableRefObject<number>;
	/**
	 * Callback to perform full recovery. This callback may depend on
	 * destroyCurrentPipeline$ internally, but the tab visibility effect
	 * should NOT directly depend on destroyCurrentPipeline$.
	 */
	performRecovery: () => void;
	/**
	 * Callback for tab inactive cleanup. Called inside the timeout handler.
	 */
	onTabInactiveCleanup: () => void;
	setSubscriptionRestartKey: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Hook that handles tab visibility changes and periodic recovery checks.
 *
 * FE-4066: This hook does not directly depend on destroyCurrentPipeline$ in its
 * effect dependencies. Having destroyCurrentPipeline$ in the dependency array
 * caused a race condition during websocket recovery:
 *
 * 1. Websocket times out -> falls back to polling
 * 2. Recovery triggers -> destroyCurrentPipeline$.next() called
 * 3. subscriptionRestartKey increments -> new Subject created in useOrderbookDataPipeline
 * 4. New Subject reference causes this effect to re-run
 * 5. Effect re-run interferes with the ongoing recovery process
 * 6. Result: websocket never recovers, stuck in polling mode
 *
 * Fix: performRecovery and onTabInactiveCleanup are passed as stable callbacks.
 * These callbacks internally use destroyCurrentPipeline$, but the effect depends
 * on the callbacks, not the Subject directly.
 */
export function useTabVisibilityAndRecovery({
	selectedMarketId,
	client,
	clientRef,
	subscriptionRef,
	currentSubscriptionMarketIdRef,
	currentSubscriptionGroupingRef,
	lastFallbackTimestamp,
	performRecovery,
	onTabInactiveCleanup,
	setSubscriptionRestartKey,
}: UseTabVisibilityAndRecoveryParams): void {
	const { orderbookSource, disableAutoSourceSwitching } =
		useOrderbookDisplayStore(
			useShallow((s) => ({
				orderbookSource: s.orderbookSource,
				disableAutoSourceSwitching: s.disableAutoSourceSwitching,
			}))
		);

	const setOrderbookStore = useOrderbookDisplayStore((s) => s.set);
	const orderbookSourceRef = useSyncedRef(orderbookSource);
	const disableAutoSourceSwitchingRef = useSyncedRef(
		disableAutoSourceSwitching
	);

	const isTabActive = useTabIsActive();
	const wasTabActiveRef = useRef(isTabActive);
	const inactiveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Tab visibility handling (see hook JSDoc for why destroyCurrentPipeline$ is not in dependencies)
	useEffect(() => {
		const tabJustBecameActive = isTabActive && !wasTabActiveRef.current;
		const tabJustBecameInactive = !isTabActive && wasTabActiveRef.current;
		wasTabActiveRef.current = isTabActive;

		if (tabJustBecameInactive) {
			if (inactiveTimeoutRef.current) clearTimeout(inactiveTimeoutRef.current);

			inactiveTimeoutRef.current = setTimeout(() => {
				onTabInactiveCleanup();
				if (subscriptionRef.current) {
					subscriptionRef.current.unsubscribe();
					subscriptionRef.current = null;
				}
				if (clientRef.current) {
					clientRef.current.destroy();
					clientRef.current = null;
				}
				currentSubscriptionMarketIdRef.current = null;
				currentSubscriptionGroupingRef.current = null;
				inactiveTimeoutRef.current = null;
				// Don't increment subscriptionRestartKey here — it would trigger
				// the main useEffect and immediately reconnect. Recovery will
				// happen via setSubscriptionRestartKey when tab becomes active again.
			}, TAB_INACTIVE_DISCONNECT_DELAY_MS);
			return;
		}

		if (tabJustBecameActive && selectedMarketId) {
			if (inactiveTimeoutRef.current) {
				clearTimeout(inactiveTimeoutRef.current);
				inactiveTimeoutRef.current = null;
				return;
			}

			// Full reconnect
			if (clientRef.current) {
				clientRef.current.destroy();
				clientRef.current = null;
			}
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
			currentSubscriptionMarketIdRef.current = null;
			currentSubscriptionGroupingRef.current = null;

			if (!disableAutoSourceSwitchingRef.current) {
				setOrderbookStore((s) => {
					s.orderbookSource = getBestAvailableOrderbookSource();
				});
			}

			setSubscriptionRestartKey((prev) => prev + 1);
			return;
		}

		// Recovery check while tab active
		if (
			isTabActive &&
			selectedMarketId &&
			currentSubscriptionMarketIdRef.current
		) {
			const isWebsocketUnhealthy =
				!ENUM_UTILS.match(
					orderbookSourceRef.current,
					OrderbookSource.WEBSOCKET
				) && !disableAutoSourceSwitchingRef.current;

			if (isWebsocketUnhealthy) {
				const timeSinceLastFallback =
					Date.now() - lastFallbackTimestamp.current;
				if (timeSinceLastFallback > FALLBACK_COOLDOWN_MS) {
					performRecovery();
				}
			} else if (tabJustBecameActive) {
				client.handleTabReturn();
			}
		}

		// Handle manual source changes (when auto-switching is disabled)
		if (disableAutoSourceSwitchingRef.current) {
			if (
				ENUM_UTILS.match(
					orderbookSourceRef.current,
					OrderbookSource.WEBSOCKET
				) &&
				selectedMarketId &&
				!currentSubscriptionMarketIdRef.current?.equals(selectedMarketId)
			) {
				currentSubscriptionMarketIdRef.current = null;
				currentSubscriptionGroupingRef.current = null;
				setSubscriptionRestartKey((prev) => prev + 1);
			}
		}
	}, [
		isTabActive,
		selectedMarketId,
		orderbookSource,
		disableAutoSourceSwitching,
		setOrderbookStore,
		client,
		performRecovery,
		// Note: destroyCurrentPipeline$ is not in this array (FE-4066)
		clientRef,
		subscriptionRef,
		currentSubscriptionMarketIdRef,
		currentSubscriptionGroupingRef,
		lastFallbackTimestamp,
		onTabInactiveCleanup,
		setSubscriptionRestartKey,
		orderbookSourceRef,
		disableAutoSourceSwitchingRef,
	]);

	// Periodic recovery check
	useEffect(() => {
		const checkRecoveryInterval = setInterval(() => {
			if (document.hidden) return; // Skip recovery attempts when tab is inactive
			const isInFallback =
				!ENUM_UTILS.match(orderbookSource, OrderbookSource.WEBSOCKET) &&
				!disableAutoSourceSwitching;

			if (isInFallback) {
				const timeSinceLastFallback =
					Date.now() - lastFallbackTimestamp.current;
				if (timeSinceLastFallback > FALLBACK_COOLDOWN_MS) {
					lastFallbackTimestamp.current = 0;
					performRecovery();
				}
			}
		}, RECOVERY_CHECK_INTERVAL_MS);

		return () => clearInterval(checkRecoveryInterval);
	}, [
		orderbookSource,
		disableAutoSourceSwitching,
		performRecovery,
		lastFallbackTimestamp,
	]);

	// Cleanup inactive timeout on unmount
	useEffect(() => {
		return () => {
			if (inactiveTimeoutRef.current) {
				clearTimeout(inactiveTimeoutRef.current);
				inactiveTimeoutRef.current = null;
			}
		};
	}, []);
}
